import enc from "crypto-js/enc-hex";
import SHA256 from "crypto-js/sha256";
import type {
  FetchOpts,
  Middleware,
  MiddlewareNextFn,
  RelayRequestAny,
} from "react-relay-network-modern";
import { RelayNetworkLayerRequestBatch } from "react-relay-network-modern";

const createGETUrl = (fetchUrl: string, persistedQuery: Record<string, unknown>) => {
  const url = new URL(fetchUrl);
  for (const [key, value] of Object.entries(persistedQuery)) {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const parameter = typeof value === "object" ? JSON.stringify(value) : String(value);
    url.searchParams.append(key, parameter);
  }

  return url.toString();
};

export const persistedQueries =
  (
    options: {
      useGETForHashedQueries?: boolean;
    } = {},
  ): Middleware =>
  (next: MiddlewareNextFn) =>
  async (request: RelayRequestAny) => {
    if (request instanceof RelayNetworkLayerRequestBatch) {
      return next(request);
    }

    const { name, operationKind, text: queryText } = request.operation;
    const persistedQuery = {
      extensions: {
        persistedQuery: {
          sha256Hash: queryText && SHA256(queryText).toString(enc),
          version: 1,
        },
      },
      operationName: name,
      variables: request.variables,
    };
    const originalMethod = request.fetchOpts.method;
    // If requested, use GET for queries (but not mutations)
    if (options.useGETForHashedQueries && operationKind === "query") {
      request.fetchOpts.method = "GET";
      delete (request.fetchOpts as Partial<FetchOpts>)?.body;
      if (request.fetchOpts.url) {
        request.fetchOpts.url = createGETUrl(request.fetchOpts.url, persistedQuery);
      }
    } else {
      request.fetchOpts.body = JSON.stringify(persistedQuery);
    }

    const response = await next(request);
    if (response.errors && response.errors.length === 1) {
      if (response.errors[0].message === "PersistedQueryNotSupported") {
        return response;
      }

      if (response.errors[0].message === "PersistedQueryNotFound") {
        request.fetchOpts.method = originalMethod;
        request.fetchOpts.body = JSON.stringify(
          Object.assign({}, persistedQuery, { query: queryText }),
        );
        return next(request);
      }
    }

    return response;
  };
